import React from 'react';
import { UserRole } from 'src/api/Api';

// Wrappers
import ProjectDetailsFetcherWrapper from './ProjectDetailsFetcherWrapper';
import BioDetailsFetcherWrapper from './BioDetailsFetcherWrapper';
import CarbonDetailsFetcherWrapper from './CarbonDetailsFetcherWrapper';
import EnvDetailsFetcherWrapper from './EnvDetailsFetcherWrapper';
import RequireSitesWrapper, { withAnalysisCheck } from './RequireSitesWrapper';
import EnvDashboard from 'src/view/Environment/EnvDashboard';
import ProjectDetails from 'src/view/Admin/Projects/ProjectDetails/ProjectDetails';
import ProjectMediaPage from 'src/view/Admin/Projects/ProjectMedia/ProjectMediaPage';
import SiteDetails from 'src/view/Admin/Projects/ProjectDetails/SiteDetails/SiteDetails';
import ProjectList from 'src/view/Admin/Projects/ProjectList';
import OrganisationsPage from 'src/view/Admin/Organisation/OrganisationsPage';
import UserUpdatePage from 'src/view/Admin/Users/UserUpdatePage';
import LandingPage from 'src/view/LandingPage';
import ChangePasswordPage from 'src/view/Profile/ChangePasswordPage';
import UpdateUserPage from 'src/view/Profile/UpdateUserPage';
import BiodiversityAnalysisDetail from 'src/view/Biodiversity/BiodiversityAnalysisDetail';
import BiodiversityCreditDetail from 'src/view/Biodiversity/BiodiversityCreditDetail';
import BiodiversityDashabordSiteInfo from 'src/view/Biodiversity/BiodiversityDashabordSiteInfo';
import CarbonAnalysisDetails from 'src/view/Carbon/CarbonAnalysisDetails';
import CarbonCredit from 'src/view/Carbon/CarbonCredit';
import CarbonDashboard from 'src/view/Carbon/CarbonDashboard';
import EnvAnalysisDetail from 'src/view/Environment/EnvAnalysisDetail';
import InteractiveMap from 'src/view/InteractiveMap/InteractiveMap';
import ProjectDetail from 'src/view/Project/ProjectDetail';
import AddSitePage from 'src/view/Sites/AddSitePage';
import SiteHome from 'src/view/Sites/SiteHome';
import Upload from 'src/view/Admin/Deployments/Upload';
import Devices from 'src/view/Admin/Devices';
import Deployments from 'src/view/Admin/Deployments/Deployments';
import MapFetcherWrapper from './MapFetcherWrapper';
import HelpSupportPage from 'src/view/HelpSupport/HelpSupportPage';
import ApiManagement from 'src/view/Admin/ApiManagement/ApiManagement';
import ToolHub from 'src/view/ToolHub';
import BioacousticPage from 'src/view/tool/bioacoustic/BioacousticPage';
import OrgMembersPage from 'src/view/Admin/Organisation/OrgMembersPage';
import OrganisationOverviewPage from 'src/view/Admin/Organisation/OrganisationOverviewPage';
import AdminToolsPage from 'src/view/Admin/AdminToolsPage';

interface AppRoute {
  path: string;
  component: React.ComponentType<any>;
  wrapper?: React.ComponentType<{ children: React.ReactNode }> | React.ComponentType<{ children: React.ReactNode }>[];
  roles: UserRole[];
  props?: any;
  withNav?: boolean;
}

export const appRoutes: AppRoute[] = [
  { path: '/tool-hub', component: ToolHub, roles: [UserRole.User, UserRole.Employee, UserRole.Admin], withNav: false },
  { path: '/landing', component: LandingPage, roles: [UserRole.User, UserRole.Employee], withNav: false },
  // Admin or Employee Routes
  { path: '/admin/projects', component: ProjectList, roles: [UserRole.Admin, UserRole.Employee] },
  { path: '/admin/deployments', component: Deployments, roles: [UserRole.Admin, UserRole.Employee] },
  { path: '/admin/project/:id', component: ProjectDetails, wrapper: ProjectDetailsFetcherWrapper, roles: [UserRole.Admin, UserRole.Employee] },
  { path: '/admin/project/:id/site/:siteId', component: SiteDetails, roles: [UserRole.Admin, UserRole.Employee] },
  { path: '/admin/project/:id/media', component: ProjectMediaPage, roles: [UserRole.Admin, UserRole.Employee] },
  { path: '/admin/deployment/:deploymentId/upload', component: Upload, roles: [UserRole.Admin, UserRole.Employee] },
  { path: '/admin/devices', component: Devices, roles: [UserRole.Admin] },
  { path: '/admin/organisations', component: OrganisationsPage, roles: [UserRole.Admin] },
  { path: '/admin/organisation/:orgId', component: OrganisationOverviewPage, roles: [UserRole.Admin] },
  { path: '/admin/organisation/:orgId/members', component: OrgMembersPage, roles: [UserRole.Admin] },
  { path: '/admin/organisations/:orgId/members', component: OrgMembersPage, roles: [UserRole.Admin] },
  { path: '/admin/tools', component: AdminToolsPage, roles: [UserRole.Admin] },
  { path: '/admin/user/:userId/update', component: UserUpdatePage, roles: [UserRole.Admin] },
  { path: '/admin/profile/change-password', component: ChangePasswordPage, roles: [UserRole.Admin, UserRole.Employee] },
  { path: '/admin/profile/update-user', component: UpdateUserPage, roles: [UserRole.Admin, UserRole.Employee] },
  { path: '/admin/api-management', component: ApiManagement, roles: [UserRole.Admin] },

  // User Routes
  { path: '/project/:id/overview', component: ProjectDetail, wrapper: ProjectDetailsFetcherWrapper, roles: [UserRole.User, UserRole.Employee], withNav: false },
  { path: '/project/:id/sites', component: SiteHome, wrapper: [ProjectDetailsFetcherWrapper, RequireSitesWrapper], roles: [UserRole.User, UserRole.Employee] },
  { path: '/project/:id/add-site', component: AddSitePage, wrapper: ProjectDetailsFetcherWrapper, roles: [UserRole.User, UserRole.Employee] },

  { path: '/project/:id/carbon-dashboard', component: CarbonDashboard, wrapper: [ProjectDetailsFetcherWrapper, withAnalysisCheck('carbon'), CarbonDetailsFetcherWrapper], roles: [UserRole.User, UserRole.Employee] },
  { path: '/project/:id/carbon-credit', component: CarbonCredit, wrapper: [ProjectDetailsFetcherWrapper, withAnalysisCheck('carbon'), CarbonDetailsFetcherWrapper], roles: [UserRole.User, UserRole.Employee] },
  { path: '/project/:id/carbon-interactive-map', component: InteractiveMap, wrapper: [ProjectDetailsFetcherWrapper, withAnalysisCheck('carbon')], roles: [UserRole.User, UserRole.Employee] },

  { path: '/project/:id/bio-diversity-dashboard', component: BiodiversityDashabordSiteInfo, wrapper: [ProjectDetailsFetcherWrapper, withAnalysisCheck('bio'), BioDetailsFetcherWrapper], roles: [UserRole.User, UserRole.Employee] },
  { path: '/project/:id/bio-diversity-credit', component: BiodiversityCreditDetail, wrapper: [ProjectDetailsFetcherWrapper, withAnalysisCheck('bio'), BioDetailsFetcherWrapper], roles: [UserRole.User, UserRole.Employee] },
  { path: '/project/:id/bio-diversity-interactive-map', component: InteractiveMap, wrapper: [ProjectDetailsFetcherWrapper, withAnalysisCheck('bio'), MapFetcherWrapper], roles: [UserRole.User, UserRole.Employee] },

  { path: '/project/:id/climate-dashboard', component: EnvDashboard, wrapper: [ProjectDetailsFetcherWrapper, withAnalysisCheck('env'), EnvDetailsFetcherWrapper], roles: [UserRole.User, UserRole.Employee] },
  { path: '/project/:id/climate-interactive-map', component: InteractiveMap, wrapper: [ProjectDetailsFetcherWrapper, withAnalysisCheck('env')], roles: [UserRole.User, UserRole.Employee] },



  { path: '/project/:id/carbon/site/:siteId', component: CarbonAnalysisDetails, wrapper: [ProjectDetailsFetcherWrapper, CarbonDetailsFetcherWrapper], roles: [UserRole.User, UserRole.Employee] },
  { path: '/project/:id/bio-diversity/site/:siteId', component: BiodiversityAnalysisDetail, wrapper: [ProjectDetailsFetcherWrapper, BioDetailsFetcherWrapper], roles: [UserRole.User, UserRole.Employee] },
  { path: '/project/:id/climate/site/:siteId', component: EnvAnalysisDetail, wrapper: [ProjectDetailsFetcherWrapper, EnvDetailsFetcherWrapper], roles: [UserRole.User, UserRole.Employee] },

  { path: '/tools/bioacoustic', component: BioacousticPage, roles: [UserRole.User, UserRole.Employee], withNav: false },

  { path: '/help-support', component: HelpSupportPage, roles: [UserRole.User, UserRole.Employee, UserRole.Admin] },

  { path: '/profile/update-user', component: UpdateUserPage, roles: [UserRole.User, UserRole.Employee, UserRole.Admin] },
  { path: '/profile/change-password', component: ChangePasswordPage, roles: [UserRole.User, UserRole.Employee, UserRole.Admin] },
];
